import { Color, Spacing } from '@persona-id/design-tokens';
import { keyBy, startCase } from 'lodash/fp';
import { FC, Fragment, useMemo } from 'react';
import styled from 'styled-components';

import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  isWorkflowFieldVariableKey,
  UsableObjectConfig,
  WORKFLOW_VARIABLE_TO_SUFFIX_TEXT,
} from 'dashboard/lib/workflows';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowVersionOutput } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';

const ObjectKey = styled.dt`
  ${paragraphMixins.mdBold}
  margin-top: ${Spacing[1]};
  word-wrap: break-word;
`;

const ObjectLabel = styled.dd.attrs<{ hasError: boolean }>(({ hasError }) => ({
  'aria-invalid': hasError,
}))<{ hasError: boolean }>`
  ${paragraphMixins.md}
  margin-top: ${Spacing[1]};
  padding-bottom: ${Spacing[1]};
  color: ${({ hasError }) => (hasError ? Color.TextDanger : 'inherit')};
`;

interface OutputsListProps {
  editable: boolean;
  errors: WorkflowError[];
  outputs: WorkflowVersionOutput[];
  usableObjects: UsableObjectConfig[];
}

const OutputsList: FC<OutputsListProps> = ({ editable, errors, outputs, usableObjects }) => {
  const usableObjectsByKey = useMemo(() => keyBy('value', usableObjects), [usableObjects]);
  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError(errors);

  const objectOutputs = useMemo(() => {
    return outputs.map(({ token, name, 'method-chain': methodChain }) => {
      if (!usableObjectsByKey[methodChain]) {
        return {
          token,
          name,
          label: methodChain,
        };
      }
      const { step, workflowObjectKey } = usableObjectsByKey[methodChain];
      return {
        token,
        name: step.name,
        label: isWorkflowFieldVariableKey(workflowObjectKey)
          ? WORKFLOW_VARIABLE_TO_SUFFIX_TEXT[workflowObjectKey]
          : startCase(workflowObjectKey),
      };
    });
  }, [outputs, usableObjectsByKey]);

  return (
    <>
      <Paragraph>
        {objectOutputs.length ? 'The following outputs will be returned:' : 'No outputs configured'}
      </Paragraph>
      {!!objectOutputs.length && (
        <>
          <Spacer height={1} />
          <dl role='list'>
            {objectOutputs.map(({ token, name, label }) => (
              <Fragment key={token}>
                <ObjectKey>Step: {name}</ObjectKey>
                <ObjectLabel hasError={hasErrorFor(`outputs.${token}`)}>
                  Output: {label}
                </ObjectLabel>
                {editable && renderErrorMessageFor(`outputs.${token}`, { narrow: true })}
                <Spacer height={1} />
              </Fragment>
            ))}
          </dl>
        </>
      )}
    </>
  );
};

export default OutputsList;
